<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  healthData: { type: Object, default: null },
  inbox: { type: Object, default: () => ({}) },
});

const { t } = useI18n();

const STATUS_COLORS = {
  connected: 'text-n-teal-11 bg-n-teal-3',
  disconnected: 'text-n-ruby-11 bg-n-ruby-3',
  error: 'text-n-amber-11 bg-n-amber-3',
};

const STATUS_ICONS = {
  connected: 'i-lucide-check-circle',
  disconnected: 'i-lucide-x-circle',
  error: 'i-lucide-alert-triangle',
};

const DETAIL_LABELS = {
  seller_id: 'Seller ID',
  group_id: 'Group ID',
  bot_user_id: 'Bot User ID',
  store_name: 'Store Name',
  business_id: 'Business ID',
  client_id: 'Client ID',
};

const statusLabel = computed(() => {
  if (!props.healthData) return t('INBOX_MGMT.CHANNEL_HEALTH.STATUS_VALUES.UNKNOWN');
  const s = props.healthData.status;
  const key = `INBOX_MGMT.CHANNEL_HEALTH.STATUS_VALUES.${(s || 'unknown').toUpperCase()}`;
  return t(key) !== key ? t(key) : s;
});

const statusColor = computed(() =>
  STATUS_COLORS[props.healthData?.status] || 'text-n-slate-11 bg-n-alpha-2'
);
const statusIcon = computed(() =>
  STATUS_ICONS[props.healthData?.status] || 'i-lucide-help-circle'
);

const channelLabel = computed(() => {
  const ct = props.inbox?.channel_type || '';
  const short = ct.replace('Channel::', '');
  return short || props.healthData?.channel_type?.replace('Channel::', '') || '';
});

const healthItems = computed(() => {
  if (!props.healthData) return [];
  const items = [];

  if (props.healthData.channel_name) {
    items.push({
      id: 'channel_name',
      label: t('INBOX_MGMT.CHANNEL_HEALTH.FIELDS.CHANNEL_NAME'),
      value: props.healthData.channel_name,
    });
  }

  if (props.healthData.token_valid != null) {
    items.push({
      id: 'token_valid',
      label: t('INBOX_MGMT.CHANNEL_HEALTH.FIELDS.TOKEN_VALID'),
      value: props.healthData.token_valid
        ? t('INBOX_MGMT.CHANNEL_HEALTH.YES')
        : t('INBOX_MGMT.CHANNEL_HEALTH.NO'),
      type: 'badge',
      badgeClass: props.healthData.token_valid
        ? 'text-n-teal-11 bg-n-teal-3'
        : 'text-n-ruby-11 bg-n-ruby-3',
    });
  }

  if (props.healthData.last_checked_at) {
    items.push({
      id: 'last_checked',
      label: t('INBOX_MGMT.CHANNEL_HEALTH.FIELDS.LAST_CHECKED'),
      value: new Date(props.healthData.last_checked_at).toLocaleString(),
    });
  }

  if (props.healthData.details) {
    Object.entries(props.healthData.details).forEach(([key, val]) => {
      if (val != null && val !== '') {
        items.push({
          id: `detail_${key}`,
          label: DETAIL_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          value: String(val),
        });
      }
    });
  }

  if (props.healthData.error) {
    items.push({
      id: 'error',
      label: t('INBOX_MGMT.CHANNEL_HEALTH.FIELDS.ERROR'),
      value: props.healthData.error,
      type: 'badge',
      badgeClass: 'text-n-ruby-11 bg-n-ruby-3',
    });
  }

  return items;
});
</script>

<template>
  <div class="gap-4 mx-6">
    <div
      class="px-5 py-5 space-y-6 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-2"
    >
      <div
        class="flex flex-col gap-5 justify-between items-start w-full md:flex-row"
      >
        <div>
          <span class="text-heading-3 text-n-slate-12">
            {{ t('INBOX_MGMT.CHANNEL_HEALTH.TITLE') }}
          </span>
          <p class="mt-1 text-body-main text-n-slate-11">
            {{ t('INBOX_MGMT.CHANNEL_HEALTH.DESCRIPTION') }}
          </p>
          <span
            v-if="channelLabel"
            class="mt-2 inline-block text-label-small text-n-slate-10"
          >
            {{ channelLabel }}
          </span>
        </div>
      </div>

      <div v-if="healthData" class="space-y-4">
        <div
          class="flex items-center gap-3 p-4 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <Icon
            :icon="statusIcon"
            class="w-6 h-6"
            :class="statusColor.split(' ')[0]"
          />
          <span
            class="inline-flex items-center px-3 py-1 text-label-small rounded-md"
            :class="statusColor"
          >
            {{ statusLabel }}
          </span>
        </div>

        <div class="grid grid-cols-1 gap-4 xs:grid-cols-2">
          <div
            v-for="item in healthItems"
            :key="item.id"
            class="flex flex-col gap-2 p-4 rounded-lg border border-n-weak bg-n-solid-1"
          >
            <span class="text-body-main font-medium text-n-slate-11">
              {{ item.label }}
            </span>
            <span
              v-if="item.type === 'badge'"
              class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md"
              :class="item.badgeClass"
            >
              {{ item.value }}
            </span>
            <span v-else class="text-label text-n-slate-12">
              {{ item.value }}
            </span>
          </div>
        </div>
      </div>

      <div v-else class="pt-8">
        <div
          class="flex justify-center items-center p-8 text-center text-n-slate-11"
        >
          <div>
            <Icon icon="i-lucide-activity" class="mb-2 w-8 h-8" />
            <p class="text-body-main text-n-slate-11">
              {{ t('INBOX_MGMT.CHANNEL_HEALTH.NO_DATA') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
